import React from "react";
import { Helmet } from "react-helmet";
import Header from "../Header";
import Footer from "../Footer";
import "./WebApplications.css";

export default function WebApplications() {
    const modules = [
        { title: "Game Admin Panels", text: "Operate players, rooms, tournaments, wallet rules, content, reports, and support workflows from one secure web application.", tag: "Operations" },
        { title: "Player Portals", text: "Build web portals for profiles, rewards, match history, leaderboards, referrals, payments, and account support.", tag: "Players" },
        { title: "Tournament Platforms", text: "Create brackets, contests, rooms, schedules, scoring, prize distribution, and real-time status views for gaming events.", tag: "Competition" },
        { title: "LiveOps Dashboards", text: "Manage events, offers, missions, notifications, economy balancing, ad settings, analytics, and retention campaigns.", tag: "LiveOps" },
        { title: "Game Analytics Apps", text: "Track revenue, DAU, retention, player cohorts, funnel drop-offs, game sessions, and monetization metrics.", tag: "Analytics" },
        { title: "Publishing Tools", text: "Control content updates, app configurations, store links, release notes, support pages, and web-based game launch flows.", tag: "Publishing" }
    ];

    const stack = [
        "React Interfaces",
        "Node.js APIs",
        "Realtime Events",
        "Role-Based Access",
        "Wallet Logic",
        "Analytics Reports",
        "Cloud Deployment",
        "Secure Admin Tools"
    ];

    const flows = [
        { step: "01", title: "Product Mapping", detail: "We map game operations, users, roles, data, player journeys, admin actions, and reporting needs." },
        { step: "02", title: "UX Architecture", detail: "We design dashboards, filters, tables, forms, states, permissions, and workflows for repeated daily use." },
        { step: "03", title: "Frontend Development", detail: "We build fast responsive interfaces with reusable components, validation, empty states, and clean navigation." },
        { step: "04", title: "Backend Integration", detail: "We connect APIs, game servers, wallets, analytics, notifications, databases, and third-party services." },
        { step: "05", title: "Launch & Support", detail: "We test permissions, data accuracy, performance, device behavior, security, deployment, and handoff." }
    ];

    const useCases = [
        "Rummy, Ludo, Poker & Card Game Admins",
        "Fantasy Sports Web Applications",
        "Esports Tournament Platforms",
        "Game Wallet & Reward Portals",
        "Real-Time Multiplayer Dashboards",
        "Game CMS & Content Tools",
        "Game Analytics & BI Panels",
        "Web-Based Game Launchers"
    ];

    const metrics = [
        { value: "24/7", label: "Game operations visibility" },
        { value: "RBAC", label: "Secure role permissions" },
        { value: "Live", label: "Realtime player and match state" },
        { value: "Scale", label: "Cloud-ready architecture" }
    ];

    const faqs = [
        {
            question: "What type of gaming web applications can Oengines build?",
            answer: "We build game admin panels, player portals, tournament platforms, LiveOps dashboards, analytics apps, game CMS tools, wallet portals, and web-based game launchers."
        },
        {
            question: "Can you connect the web app with a game server?",
            answer: "Yes. We can integrate APIs, realtime services, multiplayer backend systems, databases, wallet logic, analytics tools, and cloud infrastructure."
        },
        {
            question: "Will the web application be responsive?",
            answer: "Yes. We design responsive web applications for desktop, laptop, tablet, and mobile views, with layouts optimized for operational use."
        },
        {
            question: "Do you provide SEO for web applications?",
            answer: "For public-facing pages we add SEO metadata, headings, schema, content structure, and share previews. Private dashboards focus more on performance, security, and usability."
        }
    ];

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Gaming Web Application Development Services",
        "provider": {
            "@type": "Organization",
            "name": "Oengines Games LLP",
            "url": "https://www.oengines.com"
        },
        "serviceType": "Web Application Development",
        "areaServed": "Worldwide",
        "description": "Gaming-focused web application development services for game admin panels, player portals, LiveOps dashboards, tournament platforms, analytics tools, wallet systems, and game CMS platforms.",
        "offers": modules.map((module) => ({
            "@type": "Offer",
            "itemOffered": {
                "@type": "Service",
                "name": module.title,
                "description": module.text
            }
        }))
    };

    return (
        <>
            <Helmet>
                <title>Gaming Web Application Development Services | Game Admin Panels | Oengines</title>
                <meta name="description" content="Oengines Games LLP builds gaming web applications, game admin panels, player portals, LiveOps dashboards, tournament platforms, analytics tools, wallet systems, and game CMS platforms." />
                <meta name="keywords" content="gaming web application development, game admin panel, player portal development, tournament platform development, LiveOps dashboard, game analytics dashboard, game CMS development" />
                <link rel="canonical" href="https://www.oengines.com/web-applications" />
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
            </Helmet>
            <Header />
            <main className="webapp-page">
                <section className="webapp-hero">
                    <div className="container">
                        <div className="row align-items-center g-5">
                            <div className="col-lg-6">
                                <span className="webapp-eyebrow">Gaming Web Application Development</span>
                                <h1>Web Applications for Game Operations, Players & LiveOps</h1>
                                <p>Oengines Games LLP builds gaming-focused web applications for admin teams, player communities, tournaments, analytics, wallets, content operations, and live product management.</p>
                                <div className="webapp-actions">
                                    <a href="/contact" className="as-btn">Plan Web App <i className="fa-regular fa-arrow-right ms-2"></i></a>
                                    <a href="tel:+912614054978" className="webapp-call">Call: (0261) - 4054978</a>
                                </div>
                            </div>
                            <div className="col-lg-6">
                                <div className="webapp-console">
                                    <div className="webapp-console-top">
                                        <span></span><span></span><span></span>
                                        <strong>GameOps Console</strong>
                                    </div>
                                    <div className="webapp-console-grid">
                                        <div className="webapp-console-sidebar">
                                            <span>Players</span>
                                            <span>Matches</span>
                                            <span>Wallet</span>
                                            <span>LiveOps</span>
                                        </div>
                                        <div className="webapp-console-main">
                                            <div className="webapp-console-stat-row">
                                                {metrics.map((item) => (
                                                    <div key={item.value}>
                                                        <strong>{item.value}</strong>
                                                        <span>{item.label}</span>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="webapp-console-chart">
                                                <span></span><span></span><span></span><span></span><span></span>
                                            </div>
                                            <div className="webapp-console-table">
                                                <span></span><span></span><span></span><span></span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="webapp-modules-section space">
                    <div className="container">
                        <div className="webapp-section-heading">
                            <span>Application Modules</span>
                            <h2>Gaming Web Apps Built Around Real Product Workflows</h2>
                            <p>We design web applications for the teams that run games every day: operations, support, product, monetization, marketing, finance, and analytics.</p>
                        </div>
                        <div className="webapp-module-grid">
                            {modules.map((item) => (
                                <article className="webapp-module-card" key={item.title}>
                                    <span>{item.tag}</span>
                                    <h3>{item.title}</h3>
                                    <p>{item.text}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="webapp-platform-section space">
                    <div className="container">
                        <div className="row align-items-center g-5">
                            <div className="col-lg-6">
                                <div className="webapp-screen-stack">
                                    <img src="assets/img/GamesImg/dashboard-07.png" alt="Gaming web app dashboard" />
                                    <img src="assets/img/GamesImg/dashboard-04.png" alt="Game operations web application" />
                                    <img src="assets/img/GameDevelopment/react/efoivn-1024x541.png" alt="Web application interface" />
                                </div>
                            </div>
                            <div className="col-lg-6">
                                <div className="webapp-section-heading">
                                    <span>Game Platform Focus</span>
                                    <h2>From Admin Control to Player-Facing Web Experiences</h2>
                                    <p>Gaming web applications need more than a simple CRUD interface. They need reliable workflows for player activity, game states, economy rules, content updates, fraud checks, support actions, and reporting.</p>
                                </div>
                                <div className="webapp-usecase-grid">
                                    {useCases.map((item) => <span key={item}>{item}</span>)}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="webapp-ops-section space">
                    <div className="container">
                        <div className="webapp-section-heading text-center">
                            <span>Operations Flow</span>
                            <h2>A Web Application Structure for Live Game Products</h2>
                        </div>
                        <div className="webapp-ops-map">
                            <div>Player Data</div>
                            <i></i>
                            <div>Game Server</div>
                            <i></i>
                            <div>Admin Actions</div>
                            <i></i>
                            <div>LiveOps Rules</div>
                            <i></i>
                            <div>Reports</div>
                        </div>
                    </div>
                </section>

                <section className="webapp-stack-section space">
                    <div className="container">
                        <div className="webapp-stack-panel">
                            <div className="webapp-section-heading mb-0">
                                <span>Build Capabilities</span>
                                <h2>Frontend, Backend, Realtime & Cloud Delivery</h2>
                                <p>We can build the user interface, API layer, realtime integration, role permissions, deployment workflow, and reporting views needed for gaming web applications.</p>
                            </div>
                            <div className="webapp-stack-list">
                                {stack.map((item) => <span key={item}>{item}</span>)}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="webapp-process-section space">
                    <div className="container">
                        <div className="webapp-section-heading text-center">
                            <span>Development Process</span>
                            <h2>From Game Workflow Mapping to Launch</h2>
                        </div>
                        <div className="webapp-process-timeline">
                            {flows.map((item) => (
                                <article key={item.step}>
                                    <span>{item.step}</span>
                                    <h3>{item.title}</h3>
                                    <p>{item.detail}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="webapp-faq-section space">
                    <div className="container">
                        <div className="webapp-section-heading text-center">
                            <span>FAQ</span>
                            <h2>Gaming Web Application Questions</h2>
                        </div>
                        <div className="webapp-faq-grid">
                            {faqs.map((item) => (
                                <article key={item.question}>
                                    <h3>{item.question}</h3>
                                    <p>{item.answer}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="webapp-cta">
                    <div className="container">
                        <div className="webapp-cta-box">
                            <div>
                                <span>Build Your Game Platform</span>
                                <h2>Need a web application for your game business?</h2>
                                <p>Share your game type, admin needs, player workflows, analytics requirements, and backend setup. We will plan a web app structure that fits your operations.</p>
                            </div>
                            <a href="/contact" className="as-btn">Start Web App Project <i className="fa-regular fa-arrow-right ms-2"></i></a>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}
